/**
 * The control-message payloads — the JSON bodies behind the enums in
 * ./protocol.
 *
 * ./protocol numbers the messages and defines the two that are hot
 * (`InputPacket`, `Snapshot`); the lobby traffic that is neither hot nor
 * performance-sensitive is shaped here. Splitting them that way is deliberate:
 * the wire FORMAT of a snapshot is a compatibility surface that must not move,
 * while "what a join request contains" changes every time the lobby grows a
 * feature, and the two should not be versioned together.
 *
 * Everything below rides `encodeControl` / `decodeControl` (./codec), i.e. it is
 * UTF-8 JSON in a length-prefixed frame. That is the right trade for messages
 * that arrive a handful of times per session — a hand-rolled binary encoding of
 * a join request saves 60 bytes once and costs a decoder nobody can read.
 *
 * DOM-safe and Node-safe: types and validators only, no I/O.
 *
 * ===========================================================================
 * NOTHING OFF A WIRE IS TRUSTED
 * ===========================================================================
 *
 * Every payload here has a matching `readX` that returns a fully-formed value or
 * null. A decoded JSON body is `unknown` and has to be treated that way: the
 * server runs unattended and a malformed frame must cost a counter, not a
 * crash. The validators are the only place that conversion happens, so there is
 * exactly one thing to audit.
 */

import { CAR_BODIES, type CarBody } from '../physics/car-config';
import { isMultiplayerMode } from './modes';
import { PROTOCOL_VERSION, type MultiplayerMode, type NetEvent, type WelcomePayload } from './protocol';

// ---------------------------------------------------------------------------
// Message types this server adds on top of ./protocol's enums
// ---------------------------------------------------------------------------

/**
 * Numbered above `ServerMessage.Kick` (133) so they can be folded into that
 * enum later without renumbering anything already on the wire.
 */
export const enum LobbyMessage {
  /** Where a player is in a quick-match queue. */
  QueueStatus = 134,
  /** The room went away underneath them — reaped, failed, or ended. */
  RoomClosed = 135,
  /**
   * A chat line, relayed. `ClientMessage.Chat` is the inbound direction and
   * `ServerMessage` has no outbound counterpart, so this is it.
   */
  Chat = 136,
}

// ---------------------------------------------------------------------------
// Client -> server
// ---------------------------------------------------------------------------

/** `ClientMessage.JoinRoom`. One message covers all four ways in. */
export interface JoinRoomPayload {
  /**
   * Which room:
   *   - a code           -> join that private room
   *   - null + create    -> make a new private room and host it
   *   - null             -> enter the quick-match queue for `mode`
   */
  code: string | null;
  mode: MultiplayerMode;
  create?: boolean;
  spectator?: boolean;
  /** Preferred team. Ignored when it would unbalance the room. */
  team?: 0 | 1;
  /** Presented to reclaim a slot the server is still holding. */
  reconnect?: ReconnectClaim | null;
}

export interface ReconnectClaim {
  code: string;
  playerId: number;
  token: string;
}

/** `ClientMessage.Ready`. */
export interface ReadyPayload {
  ready: boolean;
}

/** `ClientMessage.Chat`, inbound. The server rewrites it before relaying. */
export interface ChatPayload {
  text: string;
}

// ---------------------------------------------------------------------------
// Server -> client
// ---------------------------------------------------------------------------

/**
 * `ServerMessage.Welcome`, with the two things a client cannot be told any
 * other way: which room it landed in, and the secret that gets its seat back
 * after a drop.
 *
 * Extends `WelcomePayload` rather than replacing it, so a client written against
 * ./protocol alone still reads every field it knows about.
 */
export interface WelcomeExtended extends WelcomePayload {
  /** Room code, or null while queueing. */
  code: string | null;
  /** Present it in `JoinRoomPayload.reconnect` to reclaim this slot. */
  reconnectToken: string;
  /** The world's PRNG seed. Logged by the server and echoed here so a client
   *  can record a replay that is reproducible without server logs. */
  seed: number;
}

/** `ServerMessage.Event`. Batched per tick — a goal and its kickoff are one
 *  message, not two. */
export interface EventsPayload {
  tick: number;
  events: readonly NetEvent[];
}

/** `ServerMessage.Kick`. `code` is machine-readable; `reason` is for a human. */
export interface KickPayload {
  code: KickCode;
  reason: string;
}

export type KickCode =
  | 'protocol-version'
  | 'server-full'
  | 'room-full'
  | 'room-closed'
  | 'bad-request'
  | 'timeout'
  | 'shutdown'
  /** No valid login. Multiplayer requires an account; see /api/login. */
  | 'auth';

/** `LobbyMessage.QueueStatus`. */
export interface QueueStatusPayload {
  mode: MultiplayerMode;
  /** Players waiting in this queue, including the recipient. */
  waiting: number;
  /** Players the queue is holding out for. */
  needed: number;
  /** Milliseconds this player has been waiting. */
  waitedMs: number;
}

/** `LobbyMessage.RoomClosed`. */
export interface RoomClosedPayload {
  code: string;
  reason: string;
}

/** `ServerMessage.Chat`, relayed. The sender is stamped by the server; a client
 *  never gets to say who it is. */
export interface ChatRelayPayload {
  from: number;
  displayName: string;
  text: string;
}

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asBool = (value: unknown): boolean => value === true;

/** A room code as the server writes them: see `makeJoinCode` in server/rooms.ts.
 *  Length-capped here so a 10 kB "code" never reaches a Map key. */
export const isJoinCode = (value: unknown): value is string =>
  typeof value === 'string' && value.length >= 4 && value.length <= 8 && /^[A-Z0-9]+$/.test(value);

export const readHello = (
  payload: unknown
): { displayName: string; car: CarBody; authToken: string | null } | null => {
  if (!isRecord(payload)) return null;
  if (payload.protocolVersion !== PROTOCOL_VERSION) return null;
  const car = payload.car;
  const token = payload.authToken;
  return {
    displayName: typeof payload.displayName === 'string' ? payload.displayName : 'Player',
    car: CAR_BODIES.includes(car as CarBody) ? (car as CarBody) : 'octane',
    authToken: typeof token === 'string' && token.length > 0 && token.length <= 128 ? token : null,
  };
};

export const readJoinRoom = (payload: unknown): JoinRoomPayload | null => {
  if (!isRecord(payload)) return null;
  if (!isMultiplayerMode(payload.mode)) return null;

  const code = payload.code;
  if (code !== null && code !== undefined && !isJoinCode(code)) return null;

  const team = payload.team;
  const reconnect = readReconnect(payload.reconnect);

  return {
    code: isJoinCode(code) ? code : null,
    mode: payload.mode,
    create: asBool(payload.create),
    spectator: asBool(payload.spectator),
    ...(team === 0 || team === 1 ? { team } : {}),
    reconnect,
  };
};

export const readReconnect = (value: unknown): ReconnectClaim | null => {
  if (!isRecord(value)) return null;
  if (!isJoinCode(value.code)) return null;
  if (!Number.isInteger(value.playerId)) return null;
  if (typeof value.token !== 'string' || value.token.length === 0 || value.token.length > 64) {
    return null;
  }
  return { code: value.code, playerId: value.playerId as number, token: value.token };
};

export const readReady = (payload: unknown): boolean | null =>
  isRecord(payload) && typeof payload.ready === 'boolean' ? payload.ready : null;

/** Chat is length-capped before it is ever stored, because the only bound on a
 *  relayed string is the one the server imposes. */
export const MAX_CHAT_LENGTH = 140;

export const readChat = (payload: unknown): string | null => {
  if (!isRecord(payload) || typeof payload.text !== 'string') return null;
  const text = payload.text.slice(0, MAX_CHAT_LENGTH).trim();
  return text.length === 0 ? null : text;
};
